import { EditOutlined } from '@ant-design/icons';
import { ModalProps as AModalProps } from 'antd';
import { useFormik, useFormikContext } from 'formik';
import { createContext, ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import DeclarationInput from 'components/ui/composed/declarations/formInput/DeclarationInput';
import DeclarationSelect from 'components/ui/composed/declarations/formSelect/DeclarationSelect';
import useCodelists from 'hooks/useCodelists';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import DeclarationDatepicker from 'components/ui/composed/declarations/formDatepicker/DeclarationDatepicker';
import DeclarationNumberInput from 'components/ui/composed/declarations/formInput/DeclarationNumberInput';
import CurrencySelect from 'components/ui/composed/declarations/CurrencySelect';
import Button from 'components/new-ui/base/button/Button';
import { StyledModalFooter } from 'components/new-ui/base/modal/styles';
import { H3 } from 'components/new-ui/base/typography/styles';
import Box44Button from './components/Box44Button';
import { Box44FormWrapper, Box44StyledModal } from './components/styles';
import { createBox44FieldMap, createInitialValues, getBox44StorageKey, getDateFormatForBox44 } from './box-44-utils';
import Box44Table, { Box44TableRecord } from './components/Box44Table';
import { MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { getBox44ValidationSchema } from './validation';
import { Box44FormValues, Box44Record } from './types';
import { setFieldValueAndTouch } from '../utils/setFieldValueAndTouch';

const dateFields = ['1D24', '1D23', '1D88'];

export type IBox44Context = {
    path: string;
    hasAdditionalInfo?: boolean;
    isProduct?: boolean;
    messageType?: MessageTypes;
} & Box44Record;

export const Box44Context = createContext<IBox44Context>({} as IBox44Context);
export const useBox44Context = () => useContext(Box44Context);

interface Props extends AModalProps {
    viewOnly?: boolean | undefined;
    itemNumber?: number | string;
}

const BOX_44_TITLE = 'Additional Documents and References (Box 44)';

const Modal = ({ viewOnly, open, onCancel, itemNumber, ...rest }: Props): ReactElement => {
    const { aisCodelists } = useCodelists();
    const formik = useFormikContext();
    const {
        path = '',
        documentTypeName,
        documentIdentifierName,
        measurementName,
        monetaryAmountName,
        hasAdditionalInfo,
        messageType,
        isProduct,
    } = useBox44Context();

    if (!path || !documentTypeName || !documentIdentifierName) {
        throw new Error('Box44 modal requires a Box44Context.Provider');
    }

    if (!formik) {
        throw new Error('Box44 modal requires a formik context provider');
    }

    const [showForm, setShowForm] = useState<boolean>(false);
    const [recordToEdit, setRecordToEdit] = useState<Record<string, any> | undefined>(undefined);
    const [showAdditionalFields, setShowAdditionalFields] = useState(false);

    const fields = useMemo(
        () => createBox44FieldMap(documentTypeName, documentIdentifierName, measurementName, monetaryAmountName),
        [documentTypeName, documentIdentifierName, measurementName, monetaryAmountName]
    );

    const validationSchema = useMemo(
        () => getBox44ValidationSchema({ messageType, isProduct }),
        [messageType, isProduct]
    );

    const measurementOptions = aisCodelists?.baseMeasurement ?? [];

    const initialValues = useMemo(() => createInitialValues(fields), [fields]);

    const lFormik = useFormik<Box44FormValues>({
        initialValues,
        validationSchema,
        onSubmit: () => undefined,
    });

    const codeList = useMemo(() => {
        const box44Items = (formik?.getFieldProps(path).value ?? {}) as Record<string, any>;
        const currentCodes = Object.keys(box44Items);

        if (recordToEdit) {
            const editKey = getBox44StorageKey(recordToEdit, fields);
            if (editKey) {
                const index = currentCodes.indexOf(editKey);
                if (index >= 0) currentCodes.splice(index, 1);
            }
        }

        const documentTypeField = fields.documentType;

        const usedDocumentTypes = documentTypeField
            ? currentCodes.map((key) => box44Items[key]?.[documentTypeField]).filter((v): v is string => Boolean(v))
            : [];

        const shouldNotExclude = (code: string) => !usedDocumentTypes.includes(code);

        return aisCodelists?.commonDocumentsType?.filter((l) => shouldNotExclude(l.id));
    }, [recordToEdit, formik, path, aisCodelists?.commonDocumentsType, fields]);

    const handleShowForm = () => {
        setShowForm(true);
    };
    const handleHideForm = () => {
        lFormik.resetForm();
        setRecordToEdit(undefined);
        setShowForm(false);
    };

    const handleAdd = async () => {
        const errors = await lFormik.validateForm();

        if (Object.keys(errors).length > 0) {
            lFormik.setTouched(
                {
                    [documentTypeName]: true,
                    [documentIdentifierName]: true,
                    authorityNameSubmitter: true,
                    date: true,
                    ...(measurementName ? { [measurementName]: true } : {}),
                    ...(monetaryAmountName ? { [monetaryAmountName]: true } : {}),
                    quantity: true,
                    amount: true,
                },
                true
            );
            return;
        }

        const localValues: Box44FormValues = lFormik.values;
        const codesMap: Record<string, Box44FormValues> = {
            ...((formik.getFieldMeta(path).value ?? {}) as Record<string, Box44FormValues>),
        };

        const currentKey = getBox44StorageKey(localValues, fields);

        if (!currentKey) return;

        codesMap[currentKey] = {
            [documentTypeName]: localValues?.[documentTypeName],
            [documentIdentifierName]: localValues?.[documentIdentifierName],
            ...(measurementName ? { [measurementName]: localValues?.[measurementName] } : {}),
            ...(monetaryAmountName ? { [monetaryAmountName]: localValues?.[monetaryAmountName] } : {}),
            authorityNameSubmitter: localValues?.authorityNameSubmitter,
            date: localValues?.date,
            quantity: localValues?.quantity,
            amount: localValues?.amount,
        };

        setFieldValueAndTouch(formik, path, codesMap);

        lFormik.resetForm();
        handleHideForm();
    };

    const handleEdit = async () => {
        if (!recordToEdit) return;

        const errors = await lFormik.validateForm();

        if (Object.keys(errors).length > 0) {
            lFormik.setTouched(
                {
                    [documentTypeName]: true,
                    [documentIdentifierName]: true,
                    authorityNameSubmitter: true,
                    date: true,
                    ...(measurementName ? { [measurementName]: true } : {}),
                    ...(monetaryAmountName ? { [monetaryAmountName]: true } : {}),
                    quantity: true,
                    amount: true,
                },
                true
            );
            return;
        }

        const localValues: Box44FormValues = lFormik.values;
        const originalValue: Record<string, Box44FormValues> = {
            ...((formik.getFieldMeta(path).value ?? {}) as Record<string, Box44FormValues>),
        };

        const prevKey = getBox44StorageKey(recordToEdit, fields);
        const newKey = getBox44StorageKey(localValues, fields);

        if (!prevKey || !newKey) return;

        if (prevKey !== newKey) {
            delete originalValue[prevKey];
        }

        originalValue[newKey] = {
            [documentTypeName]: localValues[documentTypeName],
            [documentIdentifierName]: localValues[documentIdentifierName],
            ...(measurementName ? { [measurementName]: localValues[measurementName] } : {}),
            ...(monetaryAmountName ? { [monetaryAmountName]: localValues[monetaryAmountName] } : {}),
            ...(localValues.authorityNameSubmitter
                ? { authorityNameSubmitter: localValues.authorityNameSubmitter }
                : {}),
            ...(localValues.date ? { date: localValues.date } : {}),
            ...(localValues.quantity !== undefined && localValues.quantity !== null
                ? { quantity: localValues.quantity }
                : {}),
            ...(localValues.amount !== undefined && localValues.amount !== null ? { amount: localValues.amount } : {}),
        };

        setFieldValueAndTouch(formik, path, originalValue);
        handleHideForm();
    };

    const handleDelete = (code: string) => {
        const values = (formik.getFieldProps(path).value ?? {}) as Record<string, any>;

        if (Array.isArray(values)) return;
        if (!code) return;

        const nextValues = { ...values };
        delete nextValues[code];

        setFieldValueAndTouch(formik, path, nextValues);
    };

    const startEditing = (code: string) => {
        const values = (formik.getFieldMeta(path).value ?? {}) as Record<string, any>;

        if (!values || Array.isArray(values)) return;
        if (!code || !values[code]) return;

        const currentItem = values[code];

        // Expand the additional options if we're editing measurement or currency
        if (!currentItem?.[documentTypeName]) {
            setShowAdditionalFields(true);
        }

        const editValues: Box44FormValues = {
            [documentTypeName]: currentItem?.[documentTypeName] ?? undefined,
            [documentIdentifierName]: currentItem?.[documentIdentifierName] ?? undefined,
            authorityNameSubmitter: currentItem?.authorityNameSubmitter,
            date: currentItem?.date,
            ...(measurementName ? { [measurementName]: currentItem?.[measurementName] ?? undefined } : {}),
            ...(monetaryAmountName ? { [monetaryAmountName]: currentItem?.[monetaryAmountName] ?? undefined } : {}),
            quantity: currentItem?.quantity,
            amount: currentItem?.amount,
        };

        lFormik.setValues(editValues);
        setRecordToEdit(currentItem);
        setShowForm(true);
    };

    const dataSource: Box44TableRecord[] = useMemo(() => {
        const data = (formik.getFieldMeta(path).value ?? {}) as Record<string, Box44FormValues>;

        if (Array.isArray(data)) return [];

        return Object.entries(data).map(([key, values]) => {
            return {
                id: key,
                code:
                    (values?.[documentTypeName] as string) ||
                    (measurementName ? (values?.[measurementName] as string) : '') ||
                    (monetaryAmountName ? (values?.[monetaryAmountName] as string) : '') ||
                    '',
                reference:
                    (values?.[documentIdentifierName] as string) ||
                    (values?.quantity !== undefined && values?.quantity !== null
                        ? Number(values.quantity).toFixed(6)
                        : '') ||
                    (values?.amount !== undefined && values?.amount !== null ? Number(values.amount).toFixed(2) : '') ||
                    '',
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.getFieldMeta(path).value, documentTypeName, documentIdentifierName]);

    const documentIdentifierFieldProps = getFormikProps(documentIdentifierName, lFormik).fieldProps;

    // Clear the states on close
    useEffect(() => {
        if (!open) {
            lFormik.resetForm();
            setShowForm(false);
            setRecordToEdit(undefined);
            setShowAdditionalFields(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <Box44StyledModal
            open={open}
            width={680}
            title={itemNumber !== undefined ? `${BOX_44_TITLE} · Item ${itemNumber}` : BOX_44_TITLE}
            onCancel={onCancel}
            footer={
                <StyledModalFooter style={{ paddingInline: 40 }}>
                    {!viewOnly && !showForm && (
                        <div>
                            <Button onClick={handleShowForm}>Add New Item</Button>
                        </div>
                    )}

                    {showForm && (
                        <div>
                            <Button onClick={handleHideForm}>Cancel</Button>
                            <Button
                                type="primary"
                                disabled={viewOnly}
                                onClick={recordToEdit !== undefined ? handleEdit : handleAdd}
                            >
                                {recordToEdit !== undefined ? (
                                    <>
                                        Edit <EditOutlined />
                                    </>
                                ) : (
                                    <>Add</>
                                )}
                            </Button>
                        </div>
                    )}
                </StyledModalFooter>
            }
            {...rest}
        >
            <>
                <div style={{ paddingInline: 16 }}>
                    <Box44Table
                        viewOnly={viewOnly}
                        onEdit={startEditing}
                        onDelete={handleDelete}
                        dataSource={dataSource}
                        onPreview={startEditing}
                    />

                    {showForm && (
                        <div style={{ marginTop: 24 }}>
                            <H3 style={{ marginBottom: 6 }}>
                                {viewOnly ? 'Item Details' : recordToEdit ? 'Edit Item' : 'Add New Item'}
                            </H3>
                            <div className="fixed-row-layout">
                                <Box44FormWrapper>
                                    <DeclarationSelect
                                        {...getFormikProps(documentTypeName, lFormik)}
                                        refNumber="2.3"
                                        label="Document Type"
                                        selectOptions={codeList}
                                        condensed
                                        codeValidation={[]}
                                        viewOnly={viewOnly}
                                    />
                                    {dateFields.includes(lFormik.getFieldMeta(documentTypeName).value as string) &&
                                    lFormik.values[documentTypeName] !== 'NAI' ? (
                                        <DeclarationDatepicker
                                            showTime
                                            showDateFormatLabel={false}
                                            onChange={(value) =>
                                                documentIdentifierFieldProps.onChange({
                                                    target: {
                                                        value: getDateFormatForBox44(value),
                                                        name: documentIdentifierFieldProps.name,
                                                    },
                                                })
                                            }
                                            {...getFormikProps(documentIdentifierName, lFormik)}
                                            refNumber="2.3"
                                            label="Box 44 Value (Reference)"
                                            condensed
                                            viewOnly={viewOnly}
                                        />
                                    ) : (
                                        <DeclarationInput
                                            viewOnly={viewOnly}
                                            maxLength={35}
                                            refNumber="2.3"
                                            label="Box 44 Value (Reference)"
                                            condensed
                                            {...getFormikProps(documentIdentifierName, lFormik)}
                                        />
                                    )}

                                    {/* Additional fields */}
                                    {hasAdditionalInfo && showAdditionalFields && (
                                        <>
                                            <DeclarationInput
                                                {...getFormikProps('authorityNameSubmitter', lFormik)}
                                                maxLength={70}
                                                refNumber="2.3"
                                                label="Issuing Authority Name"
                                                condensed
                                                viewOnly={viewOnly}
                                            />
                                            <DeclarationDatepicker
                                                {...getFormikProps('date', lFormik)}
                                                showTime
                                                showDateFormatLabel={false}
                                                onChange={(value) =>
                                                    documentIdentifierFieldProps.onChange({
                                                        target: {
                                                            value: getDateFormatForBox44(value),
                                                            name: 'date',
                                                        },
                                                    })
                                                }
                                                refNumber="2.3"
                                                label="Date of Validity"
                                                condensed
                                                viewOnly={viewOnly}
                                            />
                                            {measurementName && (
                                                <DeclarationSelect
                                                    {...getFormikProps(measurementName, lFormik)}
                                                    label="Measurement Unit"
                                                    selectOptions={measurementOptions}
                                                    refNumber="2.3"
                                                    condensed
                                                    codeValidation={[]}
                                                    viewOnly={viewOnly}
                                                />
                                            )}
                                            <DeclarationNumberInput
                                                {...getFormikProps('quantity', lFormik)}
                                                maxLength={16}
                                                precision={6}
                                                refNumber="2.3"
                                                label="Quantity"
                                                condensed
                                                viewOnly={viewOnly}
                                            />
                                            {monetaryAmountName && (
                                                <CurrencySelect
                                                    {...getFormikProps(monetaryAmountName, lFormik)}
                                                    label="Currency Code"
                                                    condensed
                                                    refNumber="2.3"
                                                    viewOnly={viewOnly}
                                                />
                                            )}

                                            <DeclarationNumberInput
                                                {...getFormikProps('amount', lFormik)}
                                                maxLength={16}
                                                precision={2}
                                                refNumber="2.3"
                                                label="Amount"
                                                condensed
                                                viewOnly={viewOnly}
                                            />
                                        </>
                                    )}
                                </Box44FormWrapper>
                            </div>
                            {hasAdditionalInfo && (
                                <div style={{ textAlign: 'center', marginTop: 16, marginBottom: 20 }}>
                                    <Button
                                        type="link"
                                        style={{ color: 'var(--brand-6)' }}
                                        onClick={() => setShowAdditionalFields((prev) => !prev)}
                                    >
                                        {showAdditionalFields ? 'Show Less Options' : 'Show More Options'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </>
        </Box44StyledModal>
    );
};

const Box44 = { Button: Box44Button, Modal };

export default Box44;
